import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  TextField,
  IconButton
} from '@mui/material';
import { QrCodeIcon, KeyboardIcon, CloseIcon } from 'icons/materialIcons';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getUserAccessories, deleteAccessory, unmapAccessory, activateAccessory, getAccessoryBySerialNumber } from 'apis/accessoryApi';
import { getCardsByUser } from 'apis/userApi';
import { useAuth } from 'contexts/AuthContext';
import AccessoryTable from './AccessoryTable';
import NotificationSnackbar from '../common/notification/NotificationSnackbar';
import ScannerErrorBoundary from 'utils/ScannerErrorBoundary';
import { isMobileDevice } from 'utils/deviceDetector';

const AccessoryDashboard = ({ onEditAccessory }) => {
  const [accessories, setAccessories] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [scannerMessage, setScannerMessage] = useState('');
  const [scannerLoading, setScannerLoading] = useState(false);
  const [processingActivation, setProcessingActivation] = useState(false);
  const [scannerState, setScannerState] = useState('idle');

  const scannerContainerRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerId = 'qr-reader-container';

  const { cardId } = useParams();
  const { authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCardLevel = location.pathname.includes('/mycards/edit/');
  const isMobile = isMobileDevice();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cardsResponse = await getCardsByUser(authState.user?.userId);
        setUserCards(cardsResponse);

        if (isCardLevel && cardId) {
          setSelectedCardId(cardId);
        }
      } catch (error) {
        setNotification({
          open: true,
          message: 'Failed to fetch cards',
          severity: 'error'
        });
      }
    };

    if (authState.user?.userId) {
      fetchData();
    }
  }, [authState.user?.userId, cardId, isCardLevel]);

  useEffect(() => {
    fetchAccessories();
  }, [authState.user?.userId, selectedCardId]);

  const fetchAccessories = async () => {
    if (!authState.user?.userId) return;

    try {
      setLoading(true);
      const response = await getUserAccessories(authState.user.userId);
      let filteredAccessories = response.accessories;

      if (selectedCardId && selectedCardId !== 'all') {
        filteredAccessories = response.accessories.filter(accessory =>
          accessory.content?.data?.cardId === selectedCardId
        );
      }

      setAccessories(filteredAccessories);
    } catch (error) {
      setError(error.message || 'Failed to fetch accessories');
      setNotification({
        open: true,
        message: error.message || 'Failed to fetch accessories',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccessory = async (accessory) => {
    try {
      await deleteAccessory(accessory.serialNumber);
      await fetchAccessories(); // Refresh the list after successful deletion
      setNotification({
        open: true,
        message: 'Accessory deleted successfully',
        severity: 'success'
      });
      return true;
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to delete accessory',
        severity: 'error'
      });
      throw new Error(error.message || 'Failed to delete accessory');
    }
  };

  const handleUnmapAccessory = async (accessory) => {
    try {
      await unmapAccessory(accessory.serialNumber);
      await fetchAccessories(); // Refresh the list after successful unmapping
      setNotification({
        open: true,
        message: 'Accessory unmapped successfully',
        severity: 'success'
      });
      return true;
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to unmap accessory',
        severity: 'error'
      });
      throw new Error(error.message || 'Failed to unmap accessory');
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const destroyScanner = () => {
    if (window.currentScanner) {
      try {
        if (typeof window.currentScanner.stop === 'function') {
          window.currentScanner.stop();
        }

        setTimeout(() => {
          try {
            window.currentScanner.clear();
            window.currentScanner = null;
          } catch (err) {
            window.currentScanner = null;
          }
          setScannerState('idle');
        }, 200);
      } catch (err) {
        window.currentScanner = null;
        setScannerState('idle');
      }
    } else {
      setScannerState('idle');
    }
  };

  const handleScanAccessory = () => {
    setSerialNumber('');
    setScannerMessage('');
    setTabValue(isMobile ? 0 : tabValue); // Force QR scanner tab on mobile
    setScannerLoading(true);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    destroyScanner();
    setTimeout(() => {
      setDialogOpen(false);
      setProcessingActivation(false);
    }, 250);
  };

  const handleDirectActivation = async (scannedSerialNumber, isLegacy = false) => {
    if (processingActivation) return;

    setProcessingActivation(true);
    setScannerMessage('Checking accessory...');

    try {
      const queryParams = isLegacy ? '?isLegacy=true' : '';
      const response = await getAccessoryBySerialNumber(scannedSerialNumber, queryParams);

      if (!response || !response.accessory) {
        setScannerMessage('Invalid accessory. Please check the serial number.');
        setTimeout(() => {
          handleDialogClose();
        }, 2000);
        return;
      }

      if (response.accessory.status === 'activated') {
        setScannerMessage('This accessory is already activated.');
        setTimeout(() => {
          handleDialogClose();
          if (response.accessory.content?.data?.cardId) {
            navigate(`/card/${response.accessory.content.data.cardId}`);
          }
        }, 1500);
        return;
      }

      if (response.accessory.status !== 'available' && response.accessory.status !== 'purchased') {
        setScannerMessage(`This accessory cannot be activated (status: ${response.accessory.status}).`);
        setTimeout(() => {
          handleDialogClose();
        }, 2000);
        return;
      }

      setScannerMessage('Activating accessory...');

      let defaultCardId = null;
      if (userCards && userCards.length > 0) {
        defaultCardId = selectedCardId !== 'all' ? selectedCardId : userCards[0]._id;
      }

      if (!defaultCardId) {
        setScannerMessage('No card available for activation');
        setTimeout(() => {
          handleDialogClose();
          navigate('/dashboard/mycards');
        }, 2000);
        return;
      }

      const activationData = {
        userId: authState.user.userId,
        contentType: 'business_card',
        contentData: {
          cardId: defaultCardId,
          type: 'card_link'
        }
      };

      const result = await activateAccessory(scannedSerialNumber, activationData);

      if (result && result.success) {
        setScannerMessage('Accessory activated successfully!');
        setTimeout(() => {
          handleDialogClose();
          if (result.accessory.content?.data?.cardId) {
            navigate(`/card/${result.accessory.content.data.cardId}`);
          } else {
            fetchAccessories(); // Refresh the accessories list
          }
        }, 1500);
      } else {
        throw new Error('Activation failed');
      }
    } catch (error) {
      setScannerMessage(`Activation failed: ${error.message || 'Unknown error'}`);
      setTimeout(() => {
        handleDialogClose();
      }, 2000);
    }
  };

  const handleQrCodeSuccess = (decodedText) => {
    let scannedSerialNumber = decodedText;
    let isLegacy = false;

    const legacyPatterns = [
      /intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
      /app\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i,
      /www\.intouchwe\.com\/view-profile\/([A-Za-z0-9-]+)/i
    ];

    for (const pattern of legacyPatterns) {
      const match = decodedText.match(pattern);
      if (match && match[1]) {
        scannedSerialNumber = match[1];
        isLegacy = true;
        break;
      }
    }

    if (!isLegacy) {
      const urlMatch = decodedText.match(/\/accessory\/scan\/([A-Za-z0-9-]+)/);
      if (urlMatch && urlMatch[1]) {
        scannedSerialNumber = urlMatch[1];
      }
    }

    setScannerMessage(`Success! ${isLegacy ? 'Legacy ID' : 'Serial number'}: ${scannedSerialNumber}`);

    if (isMobile) {
      handleDirectActivation(scannedSerialNumber, isLegacy);
    } else {
      setTimeout(() => {
        destroyScanner();
        handleDialogClose();
        const queryParams = isLegacy ? '?isLegacy=true' : '';
        navigate(`/accessory/scan/${scannedSerialNumber}${queryParams}`);
      }, 1000);
    }
  };

  const handleMockScan = () => {
    if (serialNumber) {
      const isLegacy = /^[A-Z0-9]{5,7}$/.test(serialNumber);
      const queryParams = isLegacy ? '?isLegacy=true' : '';

      if (isMobile) {
        handleDirectActivation(serialNumber, isLegacy);
      } else {
        handleDialogClose();
        navigate(`/accessory/scan/${serialNumber}${queryParams}`);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    if (scannerState === 'initializing' || scannerState === 'cleaning') {
      setScannerMessage('Please wait until scanner is ready before switching tabs...');
      return;
    }

    if (newValue !== 0) {
      setScannerState('cleaning');
      destroyScanner();
    }

    setTabValue(newValue);
  };

  useEffect(() => {
    if (!dialogOpen || tabValue !== 0) {
      destroyScanner();
      return;
    }

    setScannerLoading(true);
    setScannerState('initializing');

    const timer = setTimeout(() => {
      destroyScanner();

      const container = document.getElementById(scannerId);
      if (!container) {
        setScannerLoading(false);
        setScannerMessage("Scanner container not found. Please try again.");
        setScannerState('idle');
        return;
      }

      container.innerHTML = '';

      try {
        import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
          try {
            const config = {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              rememberLastUsedCamera: true,
            };

            window.currentScanner = new Html5QrcodeScanner(
              scannerId,
              config,
              false
            );

            window.currentScanner.render(
              handleQrCodeSuccess,
              (errorMessage) => {
              }
            );

            setScannerLoading(false);
            setScannerState('ready');
          } catch (error) {
            setScannerMessage("Could not initialize camera. Please use manual entry or check camera permissions.");
            setScannerLoading(false);
            setScannerState('idle');
          }
        }).catch(err => {
          setScannerLoading(false);
          setScannerMessage("Failed to load scanner library. Please try again or use manual entry.");
          setScannerState('idle');
        });
      } catch (error) {
        setScannerMessage("Could not set up camera. Please use manual entry.");
        setScannerLoading(false);
        setScannerState('idle');
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      destroyScanner();
    };
  }, [dialogOpen, tabValue, navigate, isMobile]);

  useEffect(() => {
    return () => {
      destroyScanner();
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && accessories.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            color: '#4f4f4f',
          }}
        >
          {isCardLevel ? 'Card Accessories' : 'My Accessories'}
        </Typography>

        {/* Scan Accessory button */}
        {!isCardLevel && (
          <Button
            variant="contained"
            startIcon={<QrCodeIcon />}
            onClick={handleScanAccessory}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: '500',
              padding: { xs: '10px 16px', sm: '8px 16px' },
              fontSize: { xs: '0.8rem', sm: '1rem' },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Scan Accessory
          </Button>
        )}
      </Box>

      <AccessoryTable
        accessories={accessories}
        userCards={userCards}
        onEditAccessory={onEditAccessory}
        onDeleteAccessory={handleDeleteAccessory}
        onUnmapAccessory={handleUnmapAccessory}
        selectedCardId={selectedCardId}
        setSelectedCardId={setSelectedCardId}
        isCardLevel={isCardLevel}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth={isMobile ? "xs" : "sm"}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: '16px', sm: '32px' },
            width: { xs: 'calc(100% - 32px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Scan Accessory</Typography>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {!isMobile && (
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                <Tab
                  icon={<QrCodeIcon />}
                  label="Scan QR Code"
                  iconPosition="start"
                />
                <Tab
                  icon={<KeyboardIcon />}
                  label="Enter Serial Number"
                  iconPosition="start"
                />
              </Tabs>
            </Paper>
          )}

          {(tabValue === 0 || isMobile) ? (
            <ScannerErrorBoundary>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minHeight: '300px',
                  width: '100%'
                }}
              >
                <div style={{ width: '100%' }}>
                  <div id={scannerId} style={{ width: '100%' }}></div>
                  {scannerLoading && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 1
                    }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Initializing camera...
                      </Typography>
                    </Box>
                  )}
                  {processingActivation && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      zIndex: 1
                    }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Processing...
                      </Typography>
                    </Box>
                  )}
                </div>
                {scannerMessage && (
                  <Typography
                    variant="body2"
                    color={
                      scannerMessage.includes("Success") ||
                        scannerMessage.includes("Activating") ||
                        scannerMessage.includes("activated") ?
                        "success.main" :
                        scannerMessage.includes("Please wait until scanner") ?
                          "info.main" :
                          "error"
                    }
                    sx={{
                      mt: 2,
                      fontWeight:
                        scannerMessage.includes("Success") ||
                          scannerMessage.includes("activated") ?
                          'bold' : 'normal'
                    }}
                  >
                    {scannerMessage}
                  </Typography>
                )}
              </Box>
            </ScannerErrorBoundary>
          ) : (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the serial number found on your accessory:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="Enter serial number"
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>

        {tabValue === 1 && !isMobile && (
          <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              sx={{
                borderColor: '#ddd',
                color: '#666',
                '&:hover': {
                  borderColor: '#bbb',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMockScan}
              variant="contained"
              disabled={!serialNumber}
              sx={{
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#333'
                }
              }}
            >
              Continue
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={handleCloseNotification}
      />
    </Box>
  );
};

export default AccessoryDashboard;